import { useRouter } from "next/router";
import { useCallback } from "react";

import { event } from "@shared/tracking";
import { splitUrl } from "@shared/utils";

/**
 * Closes the QuickInfo drawer, which lives in the url as a `quickInfo` param.
 *
 * Only that param goes: replacing the whole query would take the campaign params the visitor
 * arrived with, and the fragment has to be carried over by hand because a url object drops it
 * otherwise — the card link that opened the drawer keeps it (see CallCardLink), so closing has
 * to leave the visitor where they were rather than scrolled somewhere else.
 *
 * `router.pathname`, not `asPath`: next percent-encodes a "?" found in the pathname, which
 * would turn a campaign url into a path that 404s on reload.
 */
export default function useQuickInfoClose(callTitle?: string | null) {
  const router = useRouter();

  return useCallback(() => {
    event("quickInfo_closed", { call: callTitle });

    const query = { ...router.query };
    delete query.quickInfo;

    router.replace(
      {
        pathname: router.pathname,
        query,
        // undefined, not null: a url object with no fragment simply has none
        hash: splitUrl(router.asPath).hash ?? undefined,
      },
      undefined,
      { shallow: true },
    );
  }, [callTitle, router]);
}
